/**
 * The sign-in form, as a pure reducer.
 *
 * Four fields of state and two decisions, but the decisions are the reason this is not four
 * `useState` calls in the screen:
 *
 * · **Editing either field clears all three error surfaces at once.** The banner, the email
 *   message and the password ring describe one failed attempt; leaving any of them up while the
 *   trainer edits reads as a second, separate problem. With two independent handlers this rule
 *   gets remembered in one of them and forgotten in the other — which is exactly what happened on
 *   mobile before the banner and the ring were cleared together.
 * · **A typed value is never cleared.** Not on failure and not on retry. Wiping the password after
 *   a wrong attempt is the single most annoying thing a sign-in form can do, and it is a
 *   two-character change away at all times.
 *
 * The password reveal toggle is deliberately NOT here: it belongs to `PasswordField`, which owns it,
 * so this form has no opinion about whether the characters are currently on screen.
 *
 * Pure: no React, no DOM, no i18n. The surfaces arrive already resolved from `authErrors.ts`.
 */

import { NO_AUTH_ERROR, type AuthErrorSurfaces } from './authErrors';

export interface SignInFormState {
  email: string;
  password: string;
  submitting: boolean;
  errors: AuthErrorSurfaces;
}

export const INITIAL_SIGN_IN_FORM: SignInFormState = {
  email: '',
  password: '',
  submitting: false,
  errors: NO_AUTH_ERROR,
};

export type SignInFormEvent =
  | { type: 'EMAIL_EDITED'; value: string }
  | { type: 'PASSWORD_EDITED'; value: string }
  | { type: 'SUBMIT_STARTED' }
  | { type: 'SUBMIT_FAILED'; surfaces: AuthErrorSurfaces }
  /** The attempt finished without a failure to show — the session layer takes over from here. */
  | { type: 'SUBMIT_SUCCEEDED' };

export function signInFormReducer(
  state: SignInFormState,
  event: SignInFormEvent,
): SignInFormState {
  switch (event.type) {
    case 'EMAIL_EDITED':
      return { ...state, email: event.value, errors: NO_AUTH_ERROR };

    case 'PASSWORD_EDITED':
      return { ...state, password: event.value, errors: NO_AUTH_ERROR };

    case 'SUBMIT_STARTED':
      // The previous failure is being retried, so it stops being current now rather than when the
      // new answer arrives — otherwise the old banner sits over a spinner.
      return { ...state, submitting: true, errors: NO_AUTH_ERROR };

    case 'SUBMIT_FAILED':
      return { ...state, submitting: false, errors: event.surfaces };

    case 'SUBMIT_SUCCEEDED':
      // `submitting` stays TRUE. A successful sign-in is followed by a navigation, and dropping the
      // busy state first would flash an idle, pressable form for one frame before the route changes.
      return { ...state, errors: NO_AUTH_ERROR };
  }
}

/**
 * Whether the primary action may run. Gating an empty submit locally keeps a blank form from
 * round-tripping to Supabase and coming back as "wrong email or password", which is a misleading
 * answer to a question nobody asked.
 */
export function canSubmitSignIn(state: SignInFormState): boolean {
  return !state.submitting && state.email.trim().length > 0 && state.password.length > 0;
}
